import React, { useState } from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../utils/slugify';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import { logFailedImage } from '../lib/imageLogger';
import { EnergyIcon } from './icons/EnergyIcon'; 
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CardImage from './CardImage';

interface SimilarCardsProps {
  cards: Card[] | null;
  currentCardId: string;
}

export default function SimilarCards({ cards, currentCardId }: SimilarCardsProps) {
  const navigate = useNavigate();
  const { currency } = useCurrencyStore();
  const [currentPage, setCurrentPage] = useState(0);
  const cardsPerPage = 5;

  if (!cards || !cards.length) return null;

  const handleCardClick = (card: Card) => {
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}`);
  };

  const totalPages = Math.ceil(cards.length / cardsPerPage);
  const startIndex = currentPage * cardsPerPage;
  const visibleCards = cards.slice(startIndex, startIndex + cardsPerPage);

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold mb-4">More {cards[0]?.name?.split(' ')[0]} Cards</h2>
      <div className="relative">
        {totalPages > 1 && (
          <>
            <button
              onClick={prevPage}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-2 bg-black/80 rounded-full text-white/60 hover:text-white transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextPage}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-2 bg-black/80 rounded-full text-white/60 hover:text-white transition-colors"
              aria-label="Next page"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
        
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 px-4">
          {visibleCards.map(card => (
            <div
              key={card.id}
              className="group relative bg-white/5 border border-white/10 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-white/25 transition-all"
              onClick={() => handleCardClick(card)}
            >
              <div className="relative aspect-[2.5/3.5]">
                <CardImage
                  src={card.images.small}
                  alt={card.name}
                  cardId={card.id}
                  className="absolute inset-0 w-full h-full object-cover"
                  width={200}
                  quality={75}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                  <div className="absolute bottom-0 inset-x-0 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <p className="text-sm font-medium text-white truncate">{card.name}</p>
                        <p className="text-xs text-white/60">
                          {card.number}/{card.set.printedTotal}
                        </p>
                      </div>
                      {card.types?.[0] && (
                        <EnergyIcon type={card.types[0]} />
                      )}
                    </div>
                    <p className="text-sm font-medium text-white/80">
                      {formatPrice(card.market_price_trend_usd || card.cardmarket?.prices?.trendPrice, currency)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  currentPage === index ? 'bg-white' : 'bg-white/20'
                }`}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}